import { useCallback, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';

interface ActiveNavigationIndicator {
  left: number;
  width: number;
}

interface UseActiveNavigationIndicatorOptions {
  activeValue: false | string;
  containerRef: RefObject<HTMLElement | null>;
  isIndicatorInScrollContent?: boolean;
  itemValues: string[];
  scrollElementSelector?: string;
}

export const useActiveNavigationIndicator = ({
  activeValue,
  containerRef,
  isIndicatorInScrollContent = false,
  itemValues,
  scrollElementSelector,
}: UseActiveNavigationIndicatorOptions) => {
  const activeIndicatorElementRef = useRef<HTMLSpanElement | null>(null);
  const itemRefs = useRef(new Map<string, HTMLElement>());
  const animationFrameRef = useRef<number | null>(null);
  const lastIndicatorRef = useRef<ActiveNavigationIndicator | null>(null);
  const itemValuesKey = itemValues.join('\u0000');

  const setIndicator = useCallback((indicator: ActiveNavigationIndicator | null) => {
    const activeIndicatorElement = activeIndicatorElementRef.current;

    if (!activeIndicatorElement) {
      return;
    }

    const lastIndicator = lastIndicatorRef.current;

    if (!indicator && !lastIndicator) {
      return;
    }

    if (
      indicator
      && lastIndicator
      && indicator.left === lastIndicator.left
      && indicator.width === lastIndicator.width
    ) {
      return;
    }

    lastIndicatorRef.current = indicator;

    if (!indicator) {
      activeIndicatorElement.style.setProperty('--active-navigation-indicator-opacity', '0');
      activeIndicatorElement.style.setProperty('--active-navigation-indicator-width', '0px');
      activeIndicatorElement.style.setProperty('--active-navigation-indicator-x', '0px');
      return;
    }

    activeIndicatorElement.style.setProperty('--active-navigation-indicator-opacity', '1');
    activeIndicatorElement.style.setProperty('--active-navigation-indicator-width', `${indicator.width}px`);
    activeIndicatorElement.style.setProperty('--active-navigation-indicator-x', `${indicator.left}px`);
  }, []);

  const setActiveItemRef = useCallback(
    (itemValue: string) => (node: HTMLElement | null) => {
      if (node) {
        itemRefs.current.set(itemValue, node);
        return;
      }

      itemRefs.current.delete(itemValue);
    },
    []
  );

  const measureActiveIndicator = useCallback(() => {
    const container = containerRef.current;
    const activeItem = activeValue === false
      ? null
      : itemRefs.current.get(activeValue) ?? container?.querySelector<HTMLElement>('[aria-selected="true"]');

    if (!container || !activeItem) {
      setIndicator(null);
      return;
    }

    const scrollElement = scrollElementSelector
      ? container.querySelector<HTMLElement>(scrollElementSelector)
      : container;
    const scrollOffset = isIndicatorInScrollContent ? 0 : scrollElement?.scrollLeft ?? 0;

    setIndicator({
      left: activeItem.offsetLeft - scrollOffset,
      width: activeItem.offsetWidth,
    });
  }, [activeValue, containerRef, isIndicatorInScrollContent, scrollElementSelector, setIndicator]);

  const cancelScheduledActiveIndicatorUpdate = useCallback(() => {
    const ownerWindow = containerRef.current?.ownerDocument.defaultView ?? window;

    if (animationFrameRef.current !== null) {
      ownerWindow.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, [containerRef]);

  const updateActiveIndicator = useCallback(() => {
    const ownerWindow = containerRef.current?.ownerDocument.defaultView ?? window;

    cancelScheduledActiveIndicatorUpdate();
    animationFrameRef.current = ownerWindow.requestAnimationFrame(() => {
      animationFrameRef.current = null;
      measureActiveIndicator();
    });
  }, [cancelScheduledActiveIndicatorUpdate, containerRef, measureActiveIndicator]);

  const setActiveIndicatorRef = useCallback((node: HTMLSpanElement | null) => {
    activeIndicatorElementRef.current = node;
    lastIndicatorRef.current = null;

    if (node) {
      measureActiveIndicator();
    }
  }, [measureActiveIndicator]);

  // Navigation tabs can resize through zoom, scrolling, async labels, or route changes, so measure after layout.
  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    const ownerWindow = container.ownerDocument.defaultView ?? window;
    const ResizeObserverConstructor = ownerWindow.ResizeObserver;
    const resizeObserver = ResizeObserverConstructor
      ? new ResizeObserverConstructor(updateActiveIndicator)
      : null;
    const scrollElement = scrollElementSelector
      ? container.querySelector<HTMLElement>(scrollElementSelector)
      : container;
    const activeItem = activeValue === false ? null : itemRefs.current.get(activeValue);

    measureActiveIndicator();
    ownerWindow.addEventListener('resize', updateActiveIndicator);
    scrollElement?.addEventListener('scroll', updateActiveIndicator, { passive: true });
    resizeObserver?.observe(container);

    if (activeItem) {
      resizeObserver?.observe(activeItem);
    }

    return () => {
      cancelScheduledActiveIndicatorUpdate();
      ownerWindow.removeEventListener('resize', updateActiveIndicator);
      scrollElement?.removeEventListener('scroll', updateActiveIndicator);
      resizeObserver?.disconnect();
    };
  }, [
    activeValue,
    cancelScheduledActiveIndicatorUpdate,
    containerRef,
    itemValuesKey,
    measureActiveIndicator,
    scrollElementSelector,
    updateActiveIndicator,
  ]);

  return {
    setActiveIndicatorRef,
    setActiveItemRef,
    updateActiveIndicator,
  };
};
